<template>
  <div v-if="isLoading()" class="loader center-middle">
    <LoadingState :size="50" />
  </div>
  <div v-else-if="isErrored()">
    <ErrorState />
  </div>
  <form v-else-if="options" @submit.prevent="submit">
    <FormMessage :errors="errors" v-if="errors" />
    <p class="mb-4">
      <span class="mr-2">{{ $t("global.username") }}:</span>{{ ftpUser.user }}
    </p>
    <div class="mb-4">
      <div class="row mb-2">
        <div class="col-md-6">
          <FormPassword
            name="password"
            v-model="fields.password"
            testId="password-input"
            :placeholder="$t('global.password')"
            :label="`${$t('global.password')} *`"
            :errors="validation.fields.password.errors"
            :dirty="validation.fields.password.dirty"
            :disabled="isSubmitting"
            @input="clearGeneratedPassword"
          />
        </div>
        <div class="col-md-6">
          <FormPassword
            name="confirm-password"
            v-model="fields.confirmPassword"
            testId="confirm-password-input"
            :placeholder="$t('global.confirm-password')"
            :label="`${$t('global.confirm-password')} *`"
            :errors="validation.fields.confirmPassword.errors"
            :dirty="validation.fields.confirmPassword.dirty"
            :disabled="isSubmitting"
            @input="clearGeneratedPassword"
          />
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">
          <div class="text-secondary my-2 d-flex">
            <div class="mr-2">{{ $t("global.password-tips") }}:</div>
            <Popover :header="$t('global.password-tips')">
              <Icon icon="help" color="gamma" />
              <template #message>
                <PasswordTips />
              </template>
            </Popover>
          </div>
        </div>
        <div class="col-md-6 w-100">
          <Btn
            @click="generatePassword"
            :disabled="isSubmitting || isPasswordGenerating"
            :loading="isPasswordGenerating"
            class="w-100"
          >
            {{ $t("global.generate-password") }}
          </Btn>
        </div>
      </div>
      <Alert v-if="generatedPassword" variant="light" :dismissible="false" class="text-center mt-3">
        {{ generatedPassword }} <CopyToClipboard :text="generatedPassword" />
      </Alert>
    </div>
    <FormSelect
      name="homeDir"
      v-model="fields.homeDir"
      testId="home-dir-input"
      :label="`${$t('global.home-directory')} *`"
      :errors="validation.fields.homeDir.errors"
      :dirty="validation.fields.homeDir.dirty"
      :disabled="isSubmitting"
    >
      <option v-for="(option, index) in options" :key="index" :value="option">
        {{ option }}
      </option>
    </FormSelect>
    <div class="form-buttons">
      <Btn
        class="mr-2"
        variant="light"
        type="button"
        :disabled="isSubmitting"
        @click="cancel"
        data-test="cancel-update-ftp-user-button"
      >
        {{ $t("global.cancel") }}
      </Btn>
      <Btn
        variant="primary"
        type="submit"
        :disabled="!isSubmittable()"
        :loading="isSubmitting"
        data-test="update-ftp-user-button"
      >
        {{ $t("global.update") }}
      </Btn>
    </div>
  </form>
</template>

<script>
import { computed, onMounted, reactive, ref } from "@vue/composition-api";
import Alert from "@/components/display/Alert";
import Btn from "@/components/display/Btn";
import CopyToClipboard from "@/components/display/CopyToClipboard";
import ErrorState from "@/components/display/ItemErrorState";
import FormMessage from "@/components/display/form/FormMessage";
import FormPassword from "@/components/display/form/FormPassword";
import FormSelect from "@/components/display/form/FormSelect";
import Icon from "@/components/display/icon/Icon";
import LoadingState from "@/components/display/LoadingState";
import PasswordTips from "@/components/display/PasswordTips";
import Popover from "@/components/display/Popover";
import store from "@/store";
import useValidator from "@/utils/useValidator";
import { updateFtpUser as validationRules } from "leatherman-js/utils/validations/forms";

export default {
  name: "UpdateFtpUserForm",
  props: {
    ftpUser: {
      type: Object,
      required: true,
    },
    resourceName: {
      type: String,
      required: true,
    },
    site: {
      type: Object,
      required: true,
    },
  },
  components: {
    Alert,
    Btn,
    CopyToClipboard,
    ErrorState,
    FormMessage,
    FormPassword,
    FormSelect,
    Icon,
    LoadingState,
    Popover,
    PasswordTips,
  },
  setup(props, { emit, root }) {
    const options = computed(() => store.getters["ftp/getHomeDirOptions"]);
    const status = computed(() => store.getters["ftp/getHomeDirOptionsStatus"]);

    const cancel = () => emit("close");
    const errors = ref(null);
    const fields = reactive({
      confirmPassword: null,
      homeDir: props.ftpUser.homeDir,
      password: null,
    });
    const generatedPassword = ref(null);
    const isLoading = () => status.value.isLoading;
    const isErrored = () => status.isErrored;
    const isPasswordGenerating = ref(false);
    const isSubmittable = () => {
      if (isSubmitting.value) return false;
      if (hasNewPassword()) {
        const passwordErrors = validation.errors.filter(
          (item) => item.name === "password" || item.name === "confirmPassword"
        );
        if (passwordErrors?.length > 0) return false;
      }
      return hasNewPassword || hasNewHomeDir();
    };
    const isSubmitting = ref(false);
    const { validator } = useValidator();

    const validation = validator(
      fields,
      validationRules,
      {},
      {
        confirmPassword: {
          matchField: root.$t("validation.passwords-must-match"),
        },
        homeDir: {},
        password: {
          password: root.$t("validation.password-too-weak"),
        },
      }
    );

    const clearGeneratedPassword = () => (generatedPassword.value = null);

    const generatePassword = () => {
      isPasswordGenerating.value = true;
      store
        .dispatch("password/generate")
        .then((password) => {
          generatedPassword.value = password;
          fields.password = password;
          fields.confirmPassword = password;
        })
        .finally(() => (isPasswordGenerating.value = false));
    };

    const hasNewHomeDir = () =>
      fields.homeDir?.length > 0 && fields.homeDir !== props.ftpUser.homeDir;
    const hasNewPassword = () => fields.password?.length > 0 || fields.confirmPassword?.length > 0;

    const submit = () => {
      errors.value = null;
      if (hasNewPassword()) {
        validation
          .validate()
          .then(() => update())
          .catch(() => {});
      } else {
        update();
      }
    };

    const update = () => {
      const { homeDir, password } = fields;
      const username = props.ftpUser.username;
      isSubmitting.value = true;

      let action;
      let data;
      if (hasNewHomeDir() && hasNewPassword()) {
        action = "ftp/updateUser";
        data = { homeDir, password, username };
      } else if (hasNewHomeDir()) {
        action = "ftp/updateUserHomeDir";
        data = { homeDir, username };
      } else {
        action = "ftp/updateUserPassword";
        data = { password, username };
      }
      store
        .dispatch(action, { id: props.site.id, data })
        .then(() => {
          store.dispatch("toasts/addToast", {
            variant: "success",
            message: root.$t("toasts.ftp-user-update-success-message", {
              name: props.resourceName,
              username,
            }),
          });
          emit("close");
        })
        .catch(() => {
          store.dispatch("toasts/addToast", {
            variant: "error",
            message: root.$t("toasts.ftp-user-update-error-message", {
              name: props.resourceName,
              username,
            }),
          });
        })
        .finally(() => {
          isSubmitting.value = false;
        });
    };

    onMounted(() => {
      store.dispatch("ftp/getHomeDirOptions", props.site.id);
    });

    return {
      cancel,
      clearGeneratedPassword,
      errors,
      fields,
      generatedPassword,
      generatePassword,
      isErrored,
      isLoading,
      isPasswordGenerating,
      isSubmittable,
      isSubmitting,
      options,
      status,
      submit,
      validation,
    };
  },
};
</script>

<style lang="scss" scoped>
.form-buttons {
  text-align: right;
  padding-top: 2rem;
}
</style>
